import React, { useEffect, useMemo, useRef, useState } from "react";
import Pagination from "../../components/Pagination";
import API_ENDPOINTS from "../shared/api-endpoints";
import useQueryParams from "../shared/hooks/useQueryParams";

const DEFAULT_SORT = "Most popular";
const DEFAULT_LABELS = {
  filterBy: "Filter By:",
  clearFilters: "Clear Filters",
  all: "All",
  faqsHeading: "Frequently Asked Questions",
  chooseCustomer: "Choose a customer",
  chooseCategory: "Choose a category",
  chooseTopic: "Choose a topic",
  sortBy: "Sort By:",
  previousPage: "Previous",
  nextPage: "Next",
  emptyState: "No FAQs found.",
};

function scrollToMain() {
  if (window.jQuery) {
    const main = window.jQuery(".main");

    if (main.length) {
      window.jQuery("html, body").animate({ scrollTop: main.offset().top }, 300);
      return;
    }
  }

  const main = document.querySelector(".main");

  if (main) {
    main.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function readQueryValue(searchParams, key) {
  const value = searchParams.get(key);
  return value || "";
}

function parseEnabledTopics(rawTopics) {
  if (typeof rawTopics !== "string") {
    return [];
  }

  return rawTopics
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
}

function FilterDropdown({ buttonId, value, options, allLabel, onSelect }) {
  return (
    <div className="dropdown">
      <button
        className="btn-dropdown dropdown-toggle"
        type="button"
        id={buttonId}
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {value || allLabel}
      </button>
      <div className="dropdown-menu" aria-labelledby={buttonId}>
        <button className={`dropdown-item ${value === "" ? "active" : ""}`} type="button" onClick={() => onSelect("")}>
          {allLabel}
        </button>
        {options.map((item) => (
          <button
            key={item}
            className={`dropdown-item ${item === value ? "active" : ""}`}
            type="button"
            onClick={() => onSelect(item)}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}

function SortDropdown({ buttonId, value, options, onSelect }) {
  return (
    <div className="dropdown">
      <button
        className="btn-dropdown dropdown-toggle"
        type="button"
        id={buttonId}
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {value}
      </button>
      <div className="dropdown-menu" aria-labelledby={buttonId}>
        {options.map((item) => (
          <button
            key={item}
            className={`dropdown-item ${item === value ? "active" : ""}`}
            type="button"
            onClick={() => onSelect(item)}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FaqsPageIsland({ config }) {
  const { setParams: setQueryParams } = useQueryParams();
  const labels = { ...DEFAULT_LABELS, ...(config.labels || {}) };
  const sortOptions = useMemo(
    () => (Array.isArray(config.sortOptions) && config.sortOptions.length > 0 ? config.sortOptions : [DEFAULT_SORT]),
    [config.sortOptions],
  );
  const [filters, setFilters] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const requestedSortBy = readQueryValue(searchParams, "sortby");

    return {
      topic: readQueryValue(searchParams, "topic"),
      category: readQueryValue(searchParams, "category"),
      customer: readQueryValue(searchParams, "customer"),
      sortBy: sortOptions.includes(requestedSortBy) ? requestedSortBy : sortOptions[0],
    };
  });
  const [faqsData, setFaqsData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [enabledTopics, setEnabledTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const requestSequenceRef = useRef(0);

  const disableClearButton =
    filters.topic === "" &&
    filters.category === "" &&
    filters.customer === "" &&
    filters.sortBy === (sortOptions[0] || DEFAULT_SORT);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;

    async function loadFaqs() {
      document.documentElement.classList.add("loading");
      setIsLoading(true);

      try {
        const response = await fetch(config.faqsApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({
            nodeId: String(config.nodeId),
            sort: filters.sortBy,
            topic: filters.topic,
            category: filters.category,
            customer: filters.customer,
            pageNumber: currentPage,
            searchQuery: "",
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`FAQ request failed with ${response.status}`);
        }

        const result = await response.json();

        if (!isActive || requestSequenceRef.current !== requestId) {
          return;
        }

        const nextResults = Array.isArray(result.Results) ? result.Results : [];
        const nextPageSize = Number.parseInt(result.PageSize, 10);
        const nextPageNumber = Number.parseInt(result.PageNumber, 10);
        const nextTotalItems = Number.parseInt(result.TotalNumberOfRecords, 10);

        setFaqsData(nextResults);
        setTotalItems(Number.isFinite(nextTotalItems) ? nextTotalItems : 0);
        setPageSize(Number.isFinite(nextPageSize) && nextPageSize > 0 ? nextPageSize : 10);
        setEnabledTopics((previous) => {
          if (previous.length > 1) {
            return previous;
          }

          return parseEnabledTopics(result.EnabledTopics);
        });

        if (Number.isFinite(nextPageNumber) && nextPageNumber > 0 && nextPageNumber !== currentPage) {
          setCurrentPage(nextPageNumber);
          return;
        }

        scrollToMain();

        setQueryParams(
          {
            topic: filters.topic,
            category: filters.category,
            sortby: filters.sortBy,
            search: "",
            customer: filters.customer,
          },
          {
            path: config.baseUrl || window.location.pathname,
            reset: true,
          },
        );
      } catch (error) {
        if (!isActive || error.name === "AbortError") {
          return;
        }

        setFaqsData([]);
        setTotalItems(0);
        setEnabledTopics([]);
        window.console.error(error);
      } finally {
        if (!isActive) {
          return;
        }

        setIsLoading(false);
        document.documentElement.classList.remove("loading");
      }
    }

    loadFaqs();

    return () => {
      isActive = false;
      controller.abort();
      document.documentElement.classList.remove("loading");
    };
  }, [config, currentPage, filters, setQueryParams]);

  return (
    <>
      <div className="container mb-5">
        <div className="d-flex align-items-end">
          <h5 className="mr-auto my-0">{labels.filterBy}</h5>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            disabled={disableClearButton}
            onClick={() => {
              setFilters({
                topic: "",
                category: "",
                customer: "",
                sortBy: sortOptions[0] || DEFAULT_SORT,
              });
              setCurrentPage(1);
            }}
          >
            {labels.clearFilters}
          </button>
        </div>
        <div className="row">
          <div className="col-lg-3 col-md-6 my-2">
            <h4 className="mb-2">{labels.chooseCustomer}</h4>
            <FilterDropdown
              buttonId="customerDropdownMenuButton"
              value={filters.customer}
              options={Array.isArray(config.customers) ? config.customers : []}
              allLabel={labels.all}
              onSelect={(value) => {
                setFilters((previous) => ({ ...previous, customer: value }));
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="col-lg-3 col-md-6 my-2">
            <h4 className="mb-2">{labels.chooseCategory}</h4>
            <FilterDropdown
              buttonId="categoryDropdownMenuButton"
              value={filters.category}
              options={Array.isArray(config.categories) ? config.categories : []}
              allLabel={labels.all}
              onSelect={(value) => {
                setFilters((previous) => ({ ...previous, category: value }));
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="col-lg-3 col-md-6 my-2">
            <h4 className="mb-2">{labels.chooseTopic}</h4>
            <FilterDropdown
              buttonId="topicDropdownMenuButton"
              value={filters.topic}
              options={enabledTopics}
              allLabel={labels.all}
              onSelect={(value) => {
                setFilters((previous) => ({ ...previous, topic: value }));
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="col-lg-3 col-md-6 my-2">
            <h4 className="mb-2">{labels.sortBy}</h4>
            <SortDropdown
              buttonId="sortByDropdownMenuButton"
              value={filters.sortBy}
              options={sortOptions}
              onSelect={(value) => {
                setFilters((previous) => ({ ...previous, sortBy: value }));
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {!isLoading && faqsData.length > 0 ? (
        <div className="container">
          <h3>{labels.faqsHeading}</h3>
          <div id="accordion" className="collapse-group">
            {faqsData.map((item, index) => {
              const headingId = `heading${index}`;
              const collapseId = `collapse${index}`;

              return (
                <div className="card" key={item.nodeId || `${item.title}-${index}`}>
                  <div className="card-header" id={headingId}>
                    <button
                      className="btn-collapse icon-down-open-big collapsed"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${collapseId}`}
                      aria-expanded="false"
                      aria-controls={collapseId}
                      data-counter={item.nodeId || ""}
                      type="button"
                      onClick={async (event) => {
                        const counter = event.currentTarget.dataset.counter || "";

                        if (!counter) {
                          return;
                        }

                        try {
                          const response = await fetch(
                            `${API_ENDPOINTS.faqIncreaseViews}?nodeId=${encodeURIComponent(counter)}`,
                            { method: "GET" },
                          );
                          const result = await response.json();

                          if (result) {
                            event.currentTarget.dataset.counter = "";
                          }
                        } catch (error) {
                          window.console.error(error);
                        }
                      }}
                    >
                      {item.title}
                    </button>
                  </div>

                  <div id={collapseId} className="collapse" aria-labelledby={headingId}>
                    <div className="card-body p-3 rte-cover" dangerouslySetInnerHTML={{ __html: item.content || "" }} />
                  </div>
                </div>
              );
            })}
          </div>
          {totalItems > pageSize ? (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={(page) => setCurrentPage(page)}
                previousLabel={labels.previousPage}
                nextLabel={labels.nextPage}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {!isLoading && faqsData.length === 0 ? (
        <div className="container">
          <h3>{labels.emptyState}</h3>
        </div>
      ) : null}
    </>
  );
}
